import { Component, OnInit } from '@angular/core';
import { environment } from '../../environments/environment';
import { GlobalsVariablesService } from '../shared/service/global-variables.service';
import { Util } from '../utilitarios/util';
import { DashboardPcp, PedidoUrgenteDTO } from './dashboard.model';
import { DashboardService } from './dashboard.service';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {

    public version: string = environment.version;
    public loading = true;
    public pt_BR: any;
    public periodo: Date[] = [];

    public totalPedidos = 0;
    public emProducao = 0;
    public finalizados = 0;
    public atrasados = 0;
    public urgentes = 0;

    public dadosFases: any;
    public optionsFases: any;

    public dadosStatus: any;
    public optionsStatus: any;

    public dadosTecnicos: any;
    public optionsTecnicos: any;

    public pedidosUrgentes: PedidoUrgenteDTO[] = [];

    constructor(
        private _dashboardService: DashboardService,
        public globalsVariablesService: GlobalsVariablesService
    ) {}

    ngOnInit(): void {
        this.pt_BR = Util.traducaoDataCalendar();
        this.inicializarPeriodo();
        this.carregarDados();
    }

    private inicializarPeriodo(): void {
        const hoje = new Date();
        const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        this.periodo = [primeiroDia, ultimoDia];
    }

    onSelecionarData(): void {
        if (this.periodo && this.periodo.length === 2 && this.periodo[1] != null) {
            this.carregarDados();
        }
    }

    private formatarDataApi(data: Date): string {
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
    }

    carregarDados(): void {
        this.loading = true;

        let dataInicio: string | undefined;
        let dataFim: string | undefined;

        if (this.periodo && this.periodo.length === 2 && this.periodo[0] && this.periodo[1]) {
            dataInicio = this.formatarDataApi(this.periodo[0]);
            dataFim = this.formatarDataApi(this.periodo[1]);
        }

        this._dashboardService.carregarDashboardPcp(dataInicio, dataFim).subscribe({
            next: (data: DashboardPcp) => {
                this.totalPedidos = data.totalPedidos || 0;
                this.emProducao = data.emProducao || 0;
                this.finalizados = data.finalizados || 0;
                this.atrasados = data.atrasados || 0;
                this.urgentes = data.urgentes || 0;
                this.pedidosUrgentes = data.pedidosUrgentes || [];

                this.montarGraficoFases(data.pedidosPorFase || {});
                this.montarGraficoStatus(data.pedidosPorStatus || {});
                this.montarGraficoTecnicos(data.produtividadePorTecnico || {});
                this.loading = false;
            },
            error: () => {
                this.loading = false;
            }
        });
    }

    private montarGraficoFases(pedidosPorFase: { [fase: string]: number }): void {
        const labels = Object.keys(pedidosPorFase);
        const valores = Object.values(pedidosPorFase);

        const cores = [
            '#42A5F5', '#66BB6A', '#FFA726', '#AB47BC',
            '#26C6DA', '#EC407A', '#7E57C2', '#8D6E63',
            '#78909C', '#D4E157', '#FF7043', '#29B6F6'
        ];

        this.dadosFases = {
            labels: labels,
            datasets: [{
                label: 'Pedidos por Fase',
                data: valores,
                backgroundColor: cores.slice(0, labels.length),
                borderRadius: 6,
                maxBarThickness: 60
            }]
        };

        this.optionsFases = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx: any) => ` ${ctx.parsed.y} pedidos`
                    }
                }
            },
            scales: {
                x: {
                    ticks: { color: '#6c757d', font: { size: 11 } },
                    grid: { display: false }
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        color: '#6c757d',
                        stepSize: 1,
                        font: { size: 11 }
                    },
                    grid: { color: 'rgba(0,0,0,0.05)' }
                }
            }
        };
    }

    private montarGraficoStatus(pedidosPorStatus: { [status: string]: number }): void {
        const labels = Object.keys(pedidosPorStatus);
        const valores = Object.values(pedidosPorStatus);

        const coresStatus: { [key: string]: string } = {
            'Não Iniciado': '#9E9E9E',
            'Em Produção': '#42A5F5',
            'Pausado': '#FFA726',
            'Finalizado': '#66BB6A'
        };

        const cores = labels.map(l => coresStatus[l] || '#78909C');

        this.dadosStatus = {
            labels: labels,
            datasets: [{
                data: valores,
                backgroundColor: cores,
                hoverBackgroundColor: cores.map(c => c + 'DD'),
                borderWidth: 0
            }]
        };

        this.optionsStatus = {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '55%',
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: '#6c757d',
                        padding: 16,
                        usePointStyle: true,
                        pointStyleWidth: 12,
                        font: { size: 12 }
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (ctx: any) => {
                            const total = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
                            const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : '0';
                            return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
                        }
                    }
                }
            }
        };
    }

    private montarGraficoTecnicos(produtividadePorTecnico: { [tecnico: string]: number }): void {
        const labels = Object.keys(produtividadePorTecnico);
        const valores = Object.values(produtividadePorTecnico);

        this.dadosTecnicos = {
            labels: labels,
            datasets: [{
                label: 'Finalizados',
                data: valores,
                backgroundColor: '#42A5F5',
                borderRadius: 6,
                maxBarThickness: 30
            }]
        };

        this.optionsTecnicos = {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx: any) => ` ${ctx.parsed.x} finalizados`
                    }
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: {
                        color: '#6c757d',
                        stepSize: 1,
                        font: { size: 11 }
                    },
                    grid: { color: 'rgba(0,0,0,0.05)' }
                },
                y: {
                    ticks: { color: '#6c757d', font: { size: 12 } },
                    grid: { display: false }
                }
            }
        };
    }

    public formatoData(data: string | null | undefined): string {
        if (!data || data === 'Data a definir') return data || '-';
        const parts = data.split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return data;
    }

    public obterLabelStatus(status: number): string {
        switch (status) {
            case 1: return 'Em Produção';
            case 2: return 'Pausado';
            case 3: return 'Finalizado';
            default: return 'Não Iniciado';
        }
    }

    public obterSeverityStatus(status: number): string {
        switch (status) {
            case 1: return 'info';
            case 2: return 'warning';
            case 3: return 'success';
            default: return 'danger';
        }
    }

    public ehAtrasado(pedido: PedidoUrgenteDTO): boolean {
        if (!pedido.dataEntrega || pedido.dataEntrega === 'Data a definir') return false;
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const parts = pedido.dataEntrega.split('-');
        if (parts.length === 3) {
            const dataEntrega = new Date(
                parseInt(parts[0], 10),
                parseInt(parts[1], 10) - 1,
                parseInt(parts[2], 10)
            );
            return dataEntrega < hoje;
        }
        return false;
    }
}
